import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, message } from 'antd';
import { useTranslation } from '../../../i18n';
import { changeAccountIdentity, sendAccountIdentityVerificationCode } from '../../../store/request';
import type { AccountIdentityType } from '../../../types/account';
import { normalizeIdentity, resolveAccount } from '../../../utils/identity';
import useCountdown from '../../../hooks/useCountDown';
import { maskPhone } from './userCenterData';

type ChangeIdentityModalProps = {
  open: boolean;
  identityType: AccountIdentityType;
  oldIdentity: string;
  onClose: () => void;
  onSuccess?: () => void;
};

function isValidNewIdentity(identityType: AccountIdentityType, raw: string) {
  const credential = resolveAccount(raw);
  if (!credential) return false;
  if (identityType === 'PHONE') return 'phone' in credential;
  return 'email' in credential;
}

export function ChangeIdentityModal({
  open,
  identityType,
  oldIdentity,
  onClose,
  onSuccess
}: ChangeIdentityModalProps) {
  const { t } = useTranslation();
  const oldCountdown = useCountdown();
  const newCountdown = useCountdown();
  const [oldCode, setOldCode] = useState('');
  const [newIdentity, setNewIdentity] = useState('');
  const [newCode, setNewCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sendingOld, setSendingOld] = useState(false);
  const [sendingNew, setSendingNew] = useState(false);

  const normalizedOld = normalizeIdentity(oldIdentity);
  const oldDisplay = identityType === 'PHONE' ? maskPhone(normalizedOld) : normalizedOld || '—';

  useEffect(() => {
    if (!open) return;
    setOldCode('');
    setNewIdentity('');
    setNewCode('');
    setSubmitting(false);
    setSendingOld(false);
    setSendingNew(false);
    oldCountdown.resetCountdown();
    newCountdown.resetCountdown();
    // 仅在打开时重置
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, identityType, oldIdentity]);

  const sendOldCode = async () => {
    if (oldCountdown.isRunning || sendingOld || !normalizedOld) return;
    setSendingOld(true);
    try {
      const res = await sendAccountIdentityVerificationCode({
        identityType,
        identity: normalizedOld
      });
      if (res.code === 200) {
        message.success(t('login.code_sent'));
        oldCountdown.startCountdown();
      } else if (res.code !== -1) {
        message.error(res.message || t('login.send_failed'));
      }
    } catch {
      message.error(t('login.send_failed_retry'));
    } finally {
      setSendingOld(false);
    }
  };

  const sendNewCode = async () => {
    if (newCountdown.isRunning || sendingNew) return;
    const next = normalizeIdentity(newIdentity);
    if (!isValidNewIdentity(identityType, next)) {
      message.warning(
        identityType === 'PHONE' ? t('userCenter.phone_invalid') : t('userCenter.change_email_invalid')
      );
      return;
    }
    if (next === normalizedOld) {
      message.warning(t('userCenter.change_same_identity'));
      return;
    }
    setSendingNew(true);
    try {
      const res = await sendAccountIdentityVerificationCode({
        identityType,
        identity: next
      });
      if (res.code === 200) {
        message.success(t('login.code_sent'));
        newCountdown.startCountdown();
      } else if (res.code !== -1) {
        message.error(res.message || t('login.send_failed'));
      }
    } catch {
      message.error(t('login.send_failed_retry'));
    } finally {
      setSendingNew(false);
    }
  };

  const submit = async () => {
    if (submitting) return;
    const nextOldCode = oldCode.trim();
    const nextNewCode = newCode.trim();
    const nextNewIdentity = normalizeIdentity(newIdentity);
    if (!normalizedOld) {
      message.warning(t('userCenter.unbind_identity_missing'));
      return;
    }
    if (!/^[0-9]{6}$/.test(nextOldCode) || !/^[0-9]{6}$/.test(nextNewCode)) {
      message.warning(t('userCenter.unbind_code_invalid'));
      return;
    }
    if (!isValidNewIdentity(identityType, nextNewIdentity)) {
      message.warning(
        identityType === 'PHONE' ? t('userCenter.phone_invalid') : t('userCenter.change_email_invalid')
      );
      return;
    }
    if (nextNewIdentity === normalizedOld) {
      message.warning(t('userCenter.change_same_identity'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await changeAccountIdentity({
        identityType,
        oldIdentity: normalizedOld,
        oldCode: nextOldCode,
        newIdentity: nextNewIdentity,
        newCode: nextNewCode
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('userCenter.change_failed'));
        return;
      }
      message.success(res.message || t('userCenter.change_success'));
      onClose();
      onSuccess?.();
    } catch {
      message.error(t('userCenter.change_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={
        identityType === 'PHONE' ? t('userCenter.change_phone_title') : t('userCenter.change_email_title')
      }
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={480}
      footer={
        <div className="flex justify-end gap-2">
          <Button className="rounded-lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="primary" className="wb-btn-primary rounded-lg" loading={submitting} onClick={submit}>
            {t('userCenter.change_confirm')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 pt-1">
        {/* <p className="mb-0 text-sm leading-relaxed text-slate-500">{t('userCenter.change_hint')}</p> */}
        <div>
          <div className="mb-1.5 text-sm font-medium text-slate-700">{t('userCenter.change_old_identity')}</div>
          <Input value={oldDisplay} disabled />
        </div>
        <div>
          <div className="mb-1.5 text-sm font-medium text-slate-700">{t('userCenter.change_old_code')}</div>
          <div className="flex gap-2">
            <Input
              value={oldCode}
              maxLength={6}
              inputMode="numeric"
              placeholder={t('login.code_placeholder')}
              onChange={(event) => setOldCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            />
            <Button
              className="shrink-0 rounded-lg"
              loading={sendingOld}
              disabled={oldCountdown.isRunning}
              onClick={sendOldCode}
            >
              {oldCountdown.isRunning ? `${oldCountdown.countdown}s` : t('login.send_code')}
            </Button>
          </div>
        </div>
        <div>
          <div className="mb-1.5 text-sm font-medium text-slate-700">{t('userCenter.change_new_identity')}</div>
          <Input
            value={newIdentity}
            maxLength={320}
            placeholder={
              identityType === 'PHONE'
                ? t('userCenter.change_phone_placeholder')
                : t('userCenter.change_email_placeholder')
            }
            onChange={(event) => setNewIdentity(event.target.value)}
          />
        </div>
        <div>
          <div className="mb-1.5 text-sm font-medium text-slate-700">{t('userCenter.change_new_code')}</div>
          <div className="flex gap-2">
            <Input
              value={newCode}
              maxLength={6}
              inputMode="numeric"
              placeholder={t('login.code_placeholder')}
              onChange={(event) => setNewCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            />
            <Button
              className="shrink-0 rounded-lg"
              loading={sendingNew}
              disabled={newCountdown.isRunning}
              onClick={sendNewCode}
            >
              {newCountdown.isRunning ? `${newCountdown.countdown}s` : t('login.send_code')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
